import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Hero from "./Hero";
import StartView from "./StartView";
import Quiz from "./Quiz";
import ResultView from "./ResultView";
import { questions } from "./questions";
import "./styles.css";

const RESULTS = {
  low: {
    band: "HIGH-RISK ZONE",
    title: "Selecting or implementing a platform now carries real risk.",
    before:
      "With no consistent home for assets and no clear owner, a new DAM will likely inherit the same chaos — just in a more expensive box. ",
    strong: "The highest-leverage move is cleanup and governance first,",
    after: " so whatever platform you pick actually gets adopted.",
  },
  mid: {
    band: "BUILDING THE FOUNDATION",
    title: "You have pieces in place, but gaps that will slow an implementation.",
    before:
      "Some structure exists, but inconsistent tagging or unclear ownership tends to surface mid-implementation — usually as delays and budget creep. ",
    strong: "A focused pre-implementation pass",
    after: " closes those gaps before they become someone else's change request.",
  },
  high: {
    band: "IMPLEMENTATION-READY",
    title: "You're in a strong position — a second opinion could still sharpen it.",
    before:
      "Governance and ownership are largely in place, which puts you ahead of most organizations at this stage. ",
    strong: "An outside, platform-agnostic review",
    after:
      " is usually the highest-value step from here — catching gaps before a vendor's implementation team does.",
  },
};

const BOOKING_URL = "https://calendly.com/dam-journey-llc";

function getResult(answers) {
  const total = answers.reduce((sum, answer) => sum + answer.score, 0);

  if (total <= 5) return RESULTS.low;
  if (total <= 10) return RESULTS.mid;
  return RESULTS.high;
}

function ProspectDamQuestions() {
  const [view, setView] = useState("start");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const startQuiz = () => {
    setCurrent(0);
    setAnswers([]);
    setResult(null);
    setView("quiz");

    requestAnimationFrame(() => {
      document.getElementById("targetSection")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const sendAnswersToBackend = async submittedAnswers => {
    try {
      const apiUrl = "https://damjourney.com/api/dam-readiness-email/";

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          answers: submittedAnswers,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      console.log("Answers submitted successfully:", data);
      return data;
    } catch (error) {
      console.error("Error sending answers:", error);
      return null;
    }
  };

  const selectAnswer = async option => {
    const nextAnswers = [
      ...answers.slice(0, current),
      {
        question: questions[current].title,
        answer: option.text,
        score: option.score,
      },
    ];

    setAnswers(nextAnswers);

    if (current < questions.length - 1) {
      setCurrent(value => value + 1);
      return;
    }

    await sendAnswersToBackend(nextAnswers);
    setResult(getResult(nextAnswers));
    setView("result");
  };

  const goBack = () => {
    if (current > 0) {
      setCurrent(value => value - 1);
    }
  };

  const restart = () => {
    setCurrent(0);
    setAnswers([]);
    setResult(null);
    setView("start");
  };

  const totalScore =
    answers.length === questions.length
      ? answers.reduce((sum, answer) => sum + answer.score, 0)
      : null;

  const bookingUrl =
    totalScore !== null
      ? `${BOOKING_URL}?utm_source=readiness_check&utm_medium=web&utm_campaign=dam_readiness&utm_content=${totalScore}`
      : BOOKING_URL;

  return (
    <div className="prospect-dam-questions">
      <Helmet>
        <title>DAM Readiness Check — DAM Journey</title>
        <meta
          name="description"
          content="Where does your organization actually stand on digital asset readiness?"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, Contact"
        />
      </Helmet>

      <Hero onStart={startQuiz} />

      <section id="targetSection" className="assess">
        <div className="card">
          {view === "start" && <StartView onStart={startQuiz} />}

          {view === "quiz" && (
            <Quiz
              question={questions[current]}
              current={current}
              total={questions.length}
              onSelect={selectAnswer}
              onBack={goBack}
            />
          )}

          {view === "result" && result && (
            <ResultView result={result} bookingUrl={bookingUrl} onRestart={restart} />
          )}
        </div>
      </section>

      <footer>
        <p>DAM Journey LLC — Premier Digital Asset Management Consulting Practice</p>
        <p>&copy; 2026 DAM Journey LLC -- All right reserved</p>
      </footer>
    </div>
  );
}

export default ProspectDamQuestions;
